import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowRightLeft, BellOff, Eye, Loader2, Pencil, ShieldAlert,
  Trash2,
} from 'lucide-react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { confirmCatastrophic, toast } from '@/shared/ui/confirm'
import { explain } from '@/shared/api/problem'
import { fmtDate, fmtDateTime, humanise } from '@/shared/lib/format'
import { LANGUAGE_LABEL, languageLabel } from '@/shared/lib/language'
import { cn } from '@/lib/utils'
import { VerificationSection } from './VerificationPanel'
import { VoterTagsSection } from './VoterTagsSection'
import { UnitPath } from '@/features/geography/UnitPath'
import type { DossierSection, VoterPiiResponse } from '@/shared/api/types'
import {
  eraseVoter,
  voterDetail, voterDossier, voterPii, voterPlacementHistory, AGE_BAND_LABEL, CONFIDENCE_LABEL,
  CONSENT_PURPOSE_LABEL, CONTACT_CHANNEL_LABEL, CONTACT_OUTCOME_LABEL,
  REGISTRATION_LABEL, SECTION_LABEL, SUPPORT_BAND_LABEL, SUPPRESSION_LABEL, VERIFICATION_LABEL,
} from './api'
import { VoterFormDialog } from './VoterFormDialog'
import {
  ChannelsControl, ConsentControl, ContactControl, HouseholdControl, MobilizerControl,
  PlacementControl, ResidenceControl, SuppressionControl, SupportControl,
} from './VoterControls'

/**
 * One tab per subject, and the control that changes that subject lives inside it.
 *
 * The drawer used to be two tabs — everything the registry knows in `detail`, everything
 * the dossier assembled in `dossier` — with the acts themselves hidden behind a modal.
 * That meant reading about a voter's support in one place and changing it in another,
 * and it meant a "Record" button that opened a second dialog on top of the drawer.
 *
 * Now each tab is a subject: what is known about it (from the registry AND from the
 * dossier contributor for the same subject) followed by the control that changes it. If
 * you are looking at Contacts you can log a contact; if you are looking at Placement you
 * can correct the placement. Nothing about a subject is anywhere else.
 *
 * `sections` names the dossier section codes the tab RENDERS. `claims` names codes it
 * owns but deliberately does not render, because a purpose-built panel on the same tab
 * already says it better — `IDENTITY` duplicates the Identity fields, `PLACEMENT` is the
 * same rows `TransferHistory` draws with resolved station paths and a link to the
 * request. Claiming them keeps them out of `More`; a code no tab claims at all — a
 * contributor added later on the backend — falls through to `More` and appears without a
 * frontend change.
 */
const TABS = [
  { key: 'detail', label: 'Detail', sections: [], claims: ['IDENTITY'] },
  { key: 'support', label: 'Support', sections: ['SUPPORT'] },
  // Consent precedes Contacts because the act does: a channel-bound contact is refused
  // outright until a consent row for its purpose exists, so a tab order that puts
  // Contacts first walks the user into a 403 they cannot read their way out of.
  { key: 'consent', label: 'Consent', sections: ['CONSENT'] },
  { key: 'contacts', label: 'Contacts', sections: ['CONTACTS'] },
  { key: 'placement', label: 'Placement', sections: ['TRANSFERS'], claims: ['PLACEMENT'] },
  { key: 'residence', label: 'Residence', sections: [], claims: ['RESIDENCE'] },
  { key: 'household', label: 'Household', sections: ['HOUSEHOLD'] },
  { key: 'verification', label: 'Verification', sections: ['VERIFICATION'] },
  { key: 'turnout', label: 'Turnout', sections: ['TURNOUT'] },
  { key: 'tags', label: 'Tags', sections: ['TAGS_SEGMENTS'] },
  { key: 'intelligence', label: 'Intelligence', sections: ['INTEL_TIMELINE'] },
  { key: 'more', label: 'More', sections: [] },
] as const
type Tab = (typeof TABS)[number]['key']

/** Every section code some tab already owns, rendered or not — `more` takes the rest. */
const CLAIMED = new Set(TABS.flatMap((t) => [
  ...(t.sections as readonly string[]),
  ...(('claims' in t ? t.claims : []) as readonly string[]),
]))

/**
 * Voter detail.
 *
 * A drawer rather than a modal, for the same reason every other detail view in
 * the app is one: it overlays the list instead of replacing it, so the row the
 * user just clicked is still there when they close it.
 *
 * The PII block is the part worth reading carefully. National ID and phone are
 * NOT fetched when the drawer opens — they come from a separate endpoint, behind
 * a separate authority, and every call writes an audit row naming the caller and
 * the voter. So they are behind a button, the button says what it will do, and
 * the values disappear again when the drawer closes.
 */
/**
 * Where this voter has been moved, and by which request.
 *
 * The registry's job is to say where someone is registered *now*. This says how they got
 * there — which matters for exactly the question that prompted it: **"which of these did
 * we actually manage to get transferred?"** A recorded transfer is the only evidence that
 * the IEBC acted, and it is worth more than a status word because it names the request,
 * the date, and both stations.
 *
 * `voter_placement_history` is append-only on the backend, so a reversal appears as a
 * second row rather than as the disappearance of the first. This renders it the same
 * way: newest first, nothing collapsed.
 *
 * One call, only when the drawer is open on a voter. The registry list does NOT do this
 * per row — fifty rows would be fifty calls — which is why the list needs the server to
 * carry the marker instead.
 */
function TransferHistory({ voterId }: { voterId: string }) {
  const history = useLoad(() => voterPlacementHistory(voterId), [voterId])
  const rows = history.data ?? []

  if (history.loading && rows.length === 0) return null
  if (rows.length === 0) {
    return (
      <DrawerSection title="Transfers">
        <DrawerField label="History" value="Never moved since registration." />
      </DrawerSection>
    )
  }

  return (
    <DrawerSection title="Transfers">
      <div className="px-4 py-2 space-y-2.5">
        {rows.map((h) => (
          <div key={h.id} className="text-erp-sm">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <ArrowRightLeft className="h-3.5 w-3.5 text-chocolate shrink-0" />
                <span className="font-medium">
                  {h.reason === 'REGISTRATION' ? 'Registered' : 'Moved'}
                </span>
                <span className="text-erp-xs text-muted-foreground">
                  {humanise(h.reason)}
                </span>
              </span>
              <span className="text-erp-xs text-muted-foreground">{fmtDateTime(h.changedAt)}</span>
            </div>
            <div className="mt-1 pl-5 text-erp-xs">
              <span className="text-muted-foreground">
                {h.fromStationId
                  ? <UnitPath stationId={h.fromStationId} path={h.fromUnitPath} />
                  : 'not previously registered'}
              </span>
              <span className="mx-1.5 text-muted-foreground/60">→</span>
              <UnitPath stationId={h.toStationId} path={h.toUnitPath} />
            </div>
            {h.transferRequestId && (
              <div className="mt-1 pl-5">
                <Link
                  to={`/transfers/${h.transferRequestId}`}
                  className="text-erp-xs text-chocolate hover:underline"
                >
                  the request that recorded it
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </DrawerSection>
  )
}

export function VoterDrawer({
  voterId, onClose, onChanged,
}: {
  voterId: string | null
  onClose: () => void
  onChanged?: () => void
}) {
  const canPii = useCan('voter:read-pii')
  const canUpdate = useCan('voter:update')
  const canErase = useCan('voter:erase')
  const [tab, setTab] = useState<Tab>('detail')

  const detail = useLoad(
    () => (voterId ? voterDetail(voterId) : Promise.resolve(null)),
    [voterId],
  )
  /**
   * The dossier is assembled from every contributor bean, so it is the expensive call —
   * and `detail` needs none of it. It is fetched the first time the user leaves the
   * Detail tab and then reused across the other ten; keying it on `tab` would re-assemble
   * the whole thing on every tab click.
   */
  const [wantDossier, setWantDossier] = useState(false)
  const dossier = useLoad(
    () => (voterId && wantDossier ? voterDossier(voterId) : Promise.resolve(null)),
    [voterId, wantDossier],
  )

  const [editOpen, setEditOpen] = useState(false)

  /** Selecting a tab is also what triggers the dossier fetch. */
  const go = (t: Tab) => {
    setTab(t)
    if (t !== 'detail') setWantDossier(true)
  }

  /** Everything a control changes is on the detail payload, so re-read it after a save. */
  const saved = () => { void detail.refresh(); void dossier.refresh(); onChanged?.() }

  /**
   * Erasure. Behind a typed phrase because it is the only irreversible action on a voter
   * and because suppression is almost always what the person actually wants — an erased
   * voter can be re-imported tomorrow by a file that does not know they asked to be left
   * alone, and a suppressed one cannot.
   */
  async function erase() {
    if (!v) return
    const ok = await confirmCatastrophic({
      title: 'Erase this record permanently?',
      html: `<b>${v.displayName}</b> and everything attached to them will be removed. This `
        + 'cannot be undone, and it cascades to their intelligence subject.<br><br>'
        + 'If they asked not to be contacted, suppress them on the <b>Contacts</b> tab '
        + 'instead: '
        + 'suppression survives the next import, erasure does not.',
      phrase: 'ERASE',
      confirmText: 'Erase permanently',
    })
    if (!ok) return
    try {
      await eraseVoter(v.id)
      toast('good', 'Record erased')
      close()
      onChanged?.()
    } catch (err) {
      toast('bad', explain(err))
    }
  }
  const [pii, setPii] = useState<VoterPiiResponse | null>(null)
  const [piiError, setPiiError] = useState<string | null>(null)
  const [piiLoading, setPiiLoading] = useState(false)

  const revealPii = async () => {
    if (!voterId) return
    setPiiLoading(true)
    setPiiError(null)
    try {
      setPii(await voterPii(voterId))
    } catch (err) {
      setPiiError(explain(err))
    } finally {
      setPiiLoading(false)
    }
  }

  const close = () => {
    setPii(null)          // PII never survives the drawer
    setPiiError(null)
    setTab('detail')
    setWantDossier(false)
    onClose()
  }

  const v = detail.data

  const sections = dossier.data?.sections ?? []
  const pending = dossier.data?.pendingSections ?? []

  /** `more` earns its tab only when a contributor exists that no other tab claims. */
  const hasMore = sections.some((s) => !CLAIMED.has(s.sectionCode))
    || pending.some((p) => !CLAIMED.has(p.sectionCode))

  /**
   * The dossier sections belonging to one tab. `more` inverts the test — it takes
   * everything the other ten did not claim, which is how a contributor added on the
   * backend shows up here without a matching frontend change.
   */
  const dossierFor = (key: Tab) => {
    const codes = TABS.find((t) => t.key === key)!.sections as readonly string[]
    const mine = (code: string) => (key === 'more' ? !CLAIMED.has(code) : codes.includes(code))
    return (
      <DossierView
        loading={dossier.loading}
        error={dossier.error}
        sections={sections.filter((s) => mine(s.sectionCode))}
        pending={pending.filter((p) => mine(p.sectionCode))}
      />
    )
  }

  return (
    <>
    <Drawer
      open={Boolean(voterId)}
      onClose={close}
      width="xl"
      title={v?.displayName ?? 'Voter'}
      subtitle={v ? `${REGISTRATION_LABEL[v.registrationStatusCode] ?? v.registrationStatusCode}${
        v.voterNumber ? ` · ${v.voterNumber}` : ''}` : undefined}
      right={v ? (
        <div className="flex items-center gap-1.5">
          {canUpdate && (
            <button type="button" className="erp-btn" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {canErase && (
            <button
              type="button"
              className="erp-btn text-red-700 dark:text-red-400"
              title="Erase this record permanently"
              onClick={() => { void erase() }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : undefined}
    >
      {/* Eleven tabs will not fit at any drawer width worth having, so the strip scrolls
          sideways rather than wrapping onto a second row — a wrapped strip moves the
          content down the moment a tab is added, and the tabs stop being a fixed
          landmark. `shrink-0` is what stops flex from squeezing them instead. */}
      <div className="border-b border-border px-4 flex gap-1 overflow-x-auto">
        {TABS.filter((t) => t.key !== 'more' || hasMore).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => go(t.key)}
            className={cn(
              'shrink-0 px-3 py-2 text-erp-sm border-b-2 -mb-px whitespace-nowrap',
              tab === t.key
                ? 'border-chocolate text-chocolate font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ErrorStrip message={detail.error} />

      {detail.loading && (
        <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {v && (
        <>
          {/* Suppression is a property of the person, not of a tab: it changes what every
              outbound channel is allowed to do, so it stays on screen whichever tab is
              open rather than hiding under Contacts. */}
          {v.doNotContact && (
            <div className="px-4 py-3">
              <Note tone="bad">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <BellOff className="h-3.5 w-3.5" /> Do not contact
                </span>
                {v.doNotContactReasonCode && (
                  <> — {SUPPRESSION_LABEL[v.doNotContactReasonCode] ?? v.doNotContactReasonCode}.</>
                )}{' '}
                Every outbound channel must honour this; the backend refuses a contact
                recorded against this voter.
              </Note>
            </div>
          )}

          {/* Detail is deliberately thin: who this is, and — behind its own audited
              button — how to reach them. Everything else moved to the tab that owns it.
              It renders no dossier section at all, which is what lets the drawer open
              without assembling one. */}
          {tab === 'detail' && (
            <>
              <DrawerSection title="Identity">
                <DrawerField label="Name" value={[v.firstName, v.middleName, v.lastName].filter(Boolean).join(' ')} />
                <DrawerField label="Voter number" value={v.voterNumber} mono />
                <DrawerField label="Gender" value={humanise(v.genderCode)} />
                <DrawerField
                  label="Age band"
                  value={v.ageBandCode ? AGE_BAND_LABEL[v.ageBandCode] ?? v.ageBandCode : null}
                />
                <DrawerField label="Birth year" value={v.birthYear} />
                <DrawerField label="Language" value={languageLabel(v.preferredLanguageCode)} />
                <DrawerField
                  label="Special needs"
                  value={v.specialNeeds?.length ? v.specialNeeds.map(humanise).join(', ') : null}
                />
              </DrawerSection>

              {/* National ID and phone are a separate endpoint, a separate permission
                  and an audit entry. Never fetched on open. */}
              <DrawerSection title="Personal identifiers">
                {!canPii && (
                  <div className="px-4 py-3 text-erp-xs text-muted-foreground inline-flex items-start gap-2">
                    <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      National ID and contact numbers require <code className="font-mono">voter:read-pii</code>.
                    </span>
                  </div>
                )}
                {canPii && !pii && (
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-erp-xs text-muted-foreground">
                      Revealing these writes an audit entry naming you and this voter. They are
                      not kept when you close this panel.
                    </p>
                    {piiError && <Note tone="bad">{piiError}</Note>}
                    <button type="button" className="erp-btn" onClick={() => void revealPii()} disabled={piiLoading}>
                      {piiLoading
                        ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading…</>
                        : <><Eye className="h-3.5 w-3.5" /> Reveal and record access</>}
                    </button>
                  </div>
                )}
                {pii && (
                  <>
                    <DrawerField label="National ID" value={pii.nationalId} mono />
                    {pii.channels.map((c) => (
                      <DrawerField
                        key={c.id}
                        label={humanise(c.kindCode)}
                        mono
                        value={
                          <span className="inline-flex items-center gap-2">
                            {c.value}
                            {c.primary && <span className="text-erp-xs text-muted-foreground">primary</span>}
                            {c.verified
                              ? <span className="text-erp-xs text-emerald-700 dark:text-emerald-400">verified</span>
                              : <span className="text-erp-xs text-muted-foreground">unverified</span>}
                          </span>
                        }
                      />
                    ))}
                    {pii.channels.length === 0 && (
                      <DrawerField label="Channels" value="None recorded" />
                    )}
                  </>
                )}
              </DrawerSection>
            </>
          )}

          {tab === 'support' && (
            <>
              <DrawerSection title="Where support stands">
                <DrawerField
                  label="Score"
                  value={`${v.supportLevel} · ${SUPPORT_BAND_LABEL[v.supportBandCode] ?? v.supportBandCode}`}
                />
                <DrawerField label="Confidence" value={CONFIDENCE_LABEL[v.confidenceCode] ?? v.confidenceCode} />
                <DrawerField label="Assessed" value={fmtDateTime(v.supportAssessedAt) || 'Never'} />
              </DrawerSection>
              {canUpdate && <SupportControl voter={v} onSaved={saved} />}
              {dossierFor('support')}
            </>
          )}

          {tab === 'contacts' && (
            <>
              <DrawerSection title="Reach">
                <DrawerField label="Last contacted" value={fmtDateTime(v.lastContactedAt) || 'Never'} />
                <DrawerField label="Contacts" value={v.contactCount} />
              </DrawerSection>
              {canUpdate && <ChannelsControl voter={v} canPii={canPii} onSaved={saved} />}
              {canUpdate && <ContactControl voter={v} onSaved={saved} />}
              {/* Who is assigned to reach this voter belongs beside the log of reaching
                  them, not on a tab of its own. */}
              {canUpdate && <MobilizerControl voter={v} onSaved={saved} />}
              {canUpdate && <SuppressionControl voter={v} onSaved={saved} />}
              {dossierFor('contacts')}
            </>
          )}

          {tab === 'consent' && (
            <>
              {canUpdate && <ConsentControl voter={v} onSaved={saved} />}
              {dossierFor('consent')}
            </>
          )}

          {tab === 'placement' && (
            <>
              <DrawerSection title="Registered">
                <DrawerField
                  label="Registered at"
                  value={<UnitPath stationId={v.pollingStationId} path={v.unitPath} />}
                />
                <DrawerField label="Registration" value={REGISTRATION_LABEL[v.registrationStatusCode]} />
                <DrawerField label="Registered" value={fmtDate(v.registeredAt)} />
              </DrawerSection>
              {canUpdate && <PlacementControl voter={v} onSaved={saved} />}
              <TransferHistory voterId={v.id} />
              {dossierFor('placement')}
            </>
          )}

          {tab === 'residence' && (
            <>
              {/* Residence is separate from registration on purpose: the gap between
                  them is the evidence a transfer request rests on. */}
              <DrawerSection title="Where they live">
                <DrawerField label="Description" value={v.residenceDescription} />
                <DrawerField label="Ward" value={<UnitPath stationId={v.residenceWardId} />} />
                <DrawerField label="Source" value={humanise(v.residenceSourceCode)} />
                <DrawerField
                  label="Confidence"
                  value={v.residenceConfidence != null ? `${v.residenceConfidence}%` : null}
                />
                {v.residenceWardId && v.wardId && v.residenceWardId !== v.wardId && (
                  <div className="px-4 py-2">
                    <Note tone="info">
                      <span className="inline-flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Registered ward and residence ward differ — grounds for a transfer request.
                      </span>
                    </Note>
                  </div>
                )}
              </DrawerSection>
              {canUpdate && <ResidenceControl voter={v} onSaved={saved} />}
            </>
          )}

          {tab === 'household' && (
            <>
              {canUpdate && <HouseholdControl voter={v} onSaved={saved} />}
              {dossierFor('household')}
            </>
          )}

          {tab === 'verification' && (
            <>
              {/* No status field here: VerificationSection leads with the status, and
                  saying it twice on one tab made the state look like two facts. */}
              <VerificationSection
                voterId={v.id}
                currentStatus={v.verificationStatusCode}
                onRecorded={saved}
              />
              {dossierFor('verification')}
            </>
          )}

          {tab === 'turnout' && dossierFor('turnout')}

          {tab === 'tags' && (
            <>
              <VoterTagsSection voterId={v.id} />
              {dossierFor('tags')}
            </>
          )}

          {tab === 'intelligence' && dossierFor('intelligence')}

          {tab === 'more' && dossierFor('more')}
        </>
      )}
    </Drawer>

    <VoterFormDialog
      open={editOpen}
      editing={v}
      onClose={() => setEditOpen(false)}
      onSaved={() => { void detail.reload(); onChanged?.() }}
    />
    </>
  )
}

/**
 * The assembled dossier.
 *
 * Sections come from `DossierContributor` beans and are assembled per request,
 * so a section can arrive `ERROR` or `TIMEOUT` while the rest render — that is
 * the assembler's soft-fail, and the UI has to show it rather than pretend the
 * section is empty. A section with no contributor at all is *absent* and appears
 * under "Available from a later phase", so the shape of the product stays
 * visible without implying data exists.
 */
function DossierView({
  loading, error, sections, pending,
}: {
  loading: boolean
  error: string | null
  sections: DossierSection[]
  pending: { sectionCode: string; title: string; availableFromPhase: number }[]
}) {
  if (loading) {
    return (
      <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Assembling…
      </div>
    )
  }
  // Filtered to one tab's sections, this can legitimately come back empty — the
  // contributor ran and found nothing, or none exists yet. A blank tab reads as a bug,
  // so say it instead.
  if (!error && sections.length === 0 && pending.length === 0) {
    return (
      <div className="px-4 py-6 text-erp-sm text-muted-foreground">
        Nothing has been recorded here yet.
      </div>
    )
  }

  return (
    <>
      <ErrorStrip message={error} />
      {sections.map((s) => (
        <DrawerSection
          key={s.sectionCode}
          title={SECTION_LABEL[s.sectionCode] ?? s.title ?? humanise(s.sectionCode)}
          right={
            s.status !== 'OK' && s.status !== 'EMPTY'
              ? <span className="text-erp-xs text-red-700 dark:text-red-400">{s.status.toLowerCase()}</span>
              : undefined
          }
        >
          {s.status === 'ERROR' || s.status === 'TIMEOUT' ? (
            <div className="px-4 py-3">
              <Note tone="bad">
                This section could not be assembled ({s.status.toLowerCase()}). The rest of
                the dossier is unaffected.
              </Note>
            </div>
          ) : (() => {
            // A row whose every field was null, an empty list or a bare uuid has nothing
            // to say. Dropping it here — rather than rendering an em-dash — is what turns
            // "Tags: [] · Segments: []" into an honest "Nothing recorded."
            const shown = s.rows
              .map((r) => ({ row: r, lbl: rowLabel(r) }))
              .filter((r) => rowParts(r.row, r.lbl.key).length > 0
                || r.lbl.key !== null
                || PROSE_KEYS.some((k) => typeof r.row[k] === 'string' && r.row[k] !== ''))
            return shown.length === 0 ? (
              <div className="px-4 py-3 text-erp-xs text-muted-foreground">Nothing recorded.</div>
            ) : (
              shown.slice(0, 25).map(({ row, lbl }, i) => (
                <DrawerField
                  key={i}
                  label={lbl.text}
                  value={<RowValue row={row} labelKey={lbl.key} />}
                />
              ))
            )
          })()}
        </DrawerSection>
      ))}

      {pending.length > 0 && (
        <DrawerSection title="Available from a later phase">
          {pending.map((p) => (
            <DrawerField
              key={p.sectionCode}
              label={p.title || humanise(p.sectionCode)}
              value={<span className="text-muted-foreground">Phase {p.availableFromPhase}</span>}
            />
          ))}
        </DrawerSection>
      )}
    </>
  )
}

/**
 * Dossier rows are open maps, so pick the most label-like key available — and return
 * WHICH key was used, so the value half can drop it. Printing "HH-000001" as the label
 * and "Code: HH-000001" beside it is the same fact twice.
 */
function rowLabel(row: Record<string, unknown>): { text: string; key: string | null } {
  for (const k of ['occurredAt', 'capturedAt', 'changedAt', 'assessedAt', 'verifiedAt',
                   'recordedAt', 'createdAt', 'electionYear', 'voterNumber', 'code', 'tag']) {
    const v = row[k]
    if (v !== undefined && v !== null && v !== '') {
      return {
        text: typeof v === 'string' && v.includes('T') ? fmtDateTime(v) : String(v),
        key: k,
      }
    }
  }
  // No date, no code, nothing label-like: leave the label column blank rather than print
  // an em dash, which in every other row on this screen means "no value recorded".
  return { text: '', key: null }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/**
 * An enum code, conservatively: letters and underscores only, four characters or more.
 *
 * Digits are excluded because identifiers look exactly like enums otherwise — `NBI00000009`
 * would render as "Nbi00000009". The length floor spares two- and three-letter codes that
 * are acronyms rather than words: EN, SW, SMS. Anything a vocabulary knows is translated
 * before this test runs, so `A25_34` still becomes "25–34".
 */
const ENUM_RE = /^[A-Z]+(_[A-Z]+)*$/

/**
 * Enum vocabularies keyed by the FIELD they belong to, not pooled into one map.
 *
 * Pooling looks tidier and is wrong: `UNASSESSED` is "Unassessed" as a support band and
 * "Never assessed" as a confidence, and `DECEASED` is a registration status, a contact
 * outcome and a suppression reason. Keying by field keeps each vocabulary in its own
 * namespace, and anything unmapped still reads as prose via the fallback.
 */
const FIELD_VOCAB: Record<string, Record<string, string>> = {
  ageBand: AGE_BAND_LABEL,
  supportBand: SUPPORT_BAND_LABEL,
  band: SUPPORT_BAND_LABEL,
  confidence: CONFIDENCE_LABEL,
  registrationStatus: REGISTRATION_LABEL,
  verificationStatus: VERIFICATION_LABEL,
  method: VERIFICATION_LABEL,
  channel: CONTACT_CHANNEL_LABEL,
  outcome: CONTACT_OUTCOME_LABEL,
  purpose: CONSENT_PURPOSE_LABEL,
  suppressionReason: SUPPRESSION_LABEL,
  preferredLanguage: LANGUAGE_LABEL,
  language: LANGUAGE_LABEL,
}

/** Geography ids worth resolving to a place. Everything else that is a uuid is dropped. */
const UNIT_KEYS = new Set([
  'residenceWardId', 'fromStationId', 'toStationId', 'pollingStationId', 'stationId', 'wardId',
])

/** `residenceWardId` → "Residence ward". humanise alone lowercases the camel hump away. */
function keyLabel(k: string): string {
  return humanise(k.replace(/Id$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
}

/** SCREAMING_SNAKE → sentence case. Title Case reads like a headline, not a value. */
function sentence(code: string): string {
  const words = humanise(code)
  return words.charAt(0) + words.slice(1).toLowerCase()
}

function valueLabel(key: string, v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  if (Array.isArray(v)) return v.length === 0 ? null : v.map((x) => String(x)).join(', ')
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'object') {
    const s = JSON.stringify(v)
    return s === '{}' ? null : s
  }
  const s = String(v)
  if (UUID_RE.test(s)) return null                       // unreadable, and not actionable here
  if (s.includes('T') && !Number.isNaN(Date.parse(s))) return fmtDateTime(s)
  const known = FIELD_VOCAB[key]?.[s]
  if (known) return known
  if (ENUM_RE.test(s) && s.length >= 4) return sentence(s)
  return s
}

/**
 * One dossier row as prose.
 *
 * The rows are open maps assembled by eight different modules, so this cannot be a fixed
 * template. What it CAN do is refuse to print the three things that were making it
 * unreadable: raw uuids, screaming enum codes, and empty collections rendered as `[]`.
 * A row left with nothing to say returns null and the caller drops it, which is why an
 * untagged voter no longer shows `Tags: [] · Segments: []`.
 */
function rowParts(row: Record<string, unknown>,
                  labelKey?: string | null): { key: string; label: string; value: string }[] {
  // `notes` is prose, not a chip, and it is rendered on its own line by RowValue —
  // see PROSE_KEYS. Excluding it here also keeps it out of the six-chip cap, so a note
  // can never be the field that falls off the end.
  const skip = new Set(['id', 'occurredAt', 'capturedAt', 'changedAt', 'assessedAt',
                        'verifiedAt', 'recordedAt', 'createdAt', ...PROSE_KEYS])
  // Whatever became the row's label is not repeated in its value.
  if (labelKey) skip.add(labelKey)
  const out: { key: string; label: string; value: string }[] = []
  for (const [k, v] of Object.entries(row)) {
    if (skip.has(k)) continue
    if (UNIT_KEYS.has(k) && typeof v === 'string' && UUID_RE.test(v)) {
      out.push({ key: k, label: keyLabel(k), value: v })   // rendered as a place, not text
      continue
    }
    const label = valueLabel(k, v)
    if (label === null) continue
    out.push({ key: k, label: keyLabel(k), value: label })
  }
  return out.slice(0, 6)
}

/**
 * Free prose on a history row, as opposed to a coded value.
 *
 * These are the fields somebody typed a sentence into, and they carry the part of the
 * record that no enum could: *why* the support moved, *what* the voter actually said,
 * *which* document was sighted. Rendered as a chip among six other chips they are
 * unreadable and, past the cap, invisible — so they get their own line.
 */
const PROSE_KEYS = ['notes', 'witnessName'] as const

function RowValue({ row, labelKey }: {
  row: Record<string, unknown>
  labelKey?: string | null
}) {
  const parts = rowParts(row, labelKey)
  const prose = PROSE_KEYS
    .map((k) => (k === labelKey ? null : row[k]))
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')

  if (parts.length === 0 && prose.length === 0) {
    return <span className="text-erp-xs text-muted-foreground">—</span>
  }
  return (
    <span className="block text-erp-xs">
      {parts.length > 0 && (
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 justify-end">
          {parts.map((p, i) => (
            <span key={p.key} className="inline-flex items-center gap-1">
              <span className="text-muted-foreground">{p.label}:</span>
              {UNIT_KEYS.has(p.key)
                ? <UnitPath stationId={p.value} />
                : <span>{p.value}</span>}
              {i < parts.length - 1 && <span className="text-muted-foreground/40">·</span>}
            </span>
          ))}
        </span>
      )}
      {prose.map((text, i) => (
        <span
          key={i}
          className="mt-1 block border-l-2 border-border pl-2 text-erp-xs italic text-foreground/80 whitespace-pre-wrap text-left"
        >
          {text}
        </span>
      ))}
    </span>
  )
}
